import { ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from '@tanstack/react-router';
import EmptyWarning from '@zeus/core/EmptyWarning';
import { ActionButton, BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import MarkdownEditor, { README_EDITOR_OPTIONS } from '_/common/components/MarkdownEditor';
import { DEFAULT_README } from '_/constants/common';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import {
  useOrgCollections,
  useCollection,
  useCollectionReadme,
  useCreateCollection,
  useUpdateCollection,
} from '_/hooks/useCollections';
import { useSchemas } from '_/hooks/useSchemas';
import createSuggestedSlug from '_/utils/createSuggestedSlug';
import { validateParameter } from '_/utils/formValidations';
import { checkIfNameExists } from '_/utils/validation/nameValidator';
import { validateSlug, checkIfSlugExists, invalidSlugErrorMessage } from '_/utils/validation/slugValidator';
import { Form, Input, Select, Button, Space, Alert, notification } from 'antd';
import React, { ReactElement, useEffect, useMemo, useState, useCallback } from 'react';

import { useCancelCallback } from './hooks/NewCollection.hooks';

interface Schema {
  id: string;
  name: string;
}

function NewCollection(): ReactElement {
  const [form] = Form.useForm();
  const history = useNavigate();
  const params = useParams({ strict: false }) as Partial<{
    orgSlug: string;
    projectSlug: string;
    collectionSlug?: string;
  }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const collectionSlug = params.collectionSlug || '';
  const { setBreadcrumbs, setActionButtons } = useTopHeader();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();

  // React Query hooks
  const { data: schemas = [] } = useSchemas(orgSlug);
  const { data: collections = [] } = useOrgCollections(orgSlug);

  const isEditMode = !!collectionSlug && collectionSlug !== '_new';
  const { data: selectedCollection } = useCollection(isEditMode ? collectionSlug : undefined, selectedProject?.id);
  const { data: selectedCollectionReadme } = useCollectionReadme(selectedCollection?.id);

  const createCollectionMutation = useCreateCollection();
  const updateCollectionMutation = useUpdateCollection();

  const [collectionReadme, setCollectionReadme] = useState<string>('');
  const [collectionParams, setCollectionParams] = useState<string[]>([]);
  const [isValidParameters, setValidParameters] = useState<boolean>(false);
  const [saveCollectionErrors, setSaveCollectionErrors] = useState<any[]>([]);
  const [slugIsSame, setSlugIsSame] = useState<boolean>(true);

  // Extract stable mutation functions
  const createMutateAsync = createCollectionMutation.mutateAsync;
  const updateMutateAsync = updateCollectionMutation.mutateAsync;

  const handleBackClick = useCallback(() => {
    if (isEditMode && selectedCollection) {
      void history({
        to: `/orgs/${orgSlug}/projects/${projectSlug}/collections/${selectedCollection.slug}`,
      });
    } else {
      void history({ to: `/orgs/${orgSlug}/projects/${projectSlug}/collections` });
    }
  }, [history, orgSlug, projectSlug, isEditMode, selectedCollection]);

  const actionButtons: ActionButton[] = useMemo(
    () => [
      {
        onClick: handleBackClick,
        icon: <ArrowLeftOutlined />,
        label: 'Back',
      },
    ],
    [handleBackClick],
  );

  // Set up header with breadcrumbs and action buttons
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
    ];

    if (isEditMode && selectedCollection) {
      breadcrumbs.push(
        {
          title: selectedCollection.name,
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollection.slug}`,
        },
        { title: 'Edit' },
      );
    } else {
      breadcrumbs.push({ title: 'New Collection' });
    }

    setBreadcrumbs(breadcrumbs);
    setActionButtons(actionButtons);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [
    setBreadcrumbs,
    setActionButtons,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    isEditMode,
    selectedCollection,
    actionButtons,
  ]);

  // Initialize form when selectedCollection is loaded
  useEffect(() => {
    if (selectedCollection && isEditMode) {
      form.setFieldsValue({
        name: selectedCollection.name,
        slug: selectedCollection.slug,
        schemaId: selectedCollection.schemaId,
      });
      setCollectionParams(selectedCollection.parameters || []);
    } else if (!isEditMode) {
      form.setFieldsValue({
        name: '',
        slug: '',
        schemaId: '',
      });
      setCollectionParams(['locale', 'domain']);
      setCollectionReadme(DEFAULT_README);
    }
  }, [selectedCollection, isEditMode, form]);

  useEffect(() => {
    if (selectedCollectionReadme) {
      setCollectionReadme(selectedCollectionReadme);
    }
  }, [selectedCollectionReadme]);

  useEffect(() => {
    if (!collectionParams?.length) {
      setValidParameters(false);
      return;
    }

    const allValid = collectionParams.reduce((valid: boolean, param: string) => valid && validateParameter(param), true);

    setValidParameters(allValid);
  }, [collectionParams]);

  const onCollectionParamsChange = (params: string[]) => {
    setCollectionParams(params);
  };

  const onCollectionReadmeChange = (value: string) => {
    setCollectionReadme(value);
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (slugIsSame && !isEditMode) {
      form.setFieldsValue({
        name: name,
        slug: createSuggestedSlug(name),
      });
    }
  };

  const onSlugChange = () => {
    if (!isEditMode) {
      const name = form.getFieldValue('name');
      const slug = form.getFieldValue('slug');
      setSlugIsSame(slug === createSuggestedSlug(name));
    }
  };

  const onFinishFailed = () => {};

  const onFinish = async (values: any) => {
    setSaveCollectionErrors([]);

    if (!selectedProject) {
      console.error('No selectedProject available');
      setSaveCollectionErrors([{ type: 'api', error: 'Project not loaded. Please refresh the page.' }]);
      return;
    }

    if (isEditMode && selectedCollection) {
      // Validation for edit mode
      const updatedCollection: any = {};
      if (selectedCollection.name !== values.name) {
        updatedCollection.name = values.name;
      }
      if (selectedCollection.parameters?.join(',') !== collectionParams.join(',')) {
        updatedCollection.parameters = collectionParams;
      }

      const updateParams: any = {
        collectionId: selectedCollection.id,
      };

      if (Object.keys(updatedCollection).length > 0) {
        updateParams.collection = updatedCollection;
      }

      if (selectedCollectionReadme !== collectionReadme) {
        updateParams.collectionReadme = collectionReadme;
      }

      try {
        await updateMutateAsync(updateParams);
        notification.success({
          message: 'Collection Saved',
        });
        void history({
          to: `/orgs/${orgSlug}/projects/${projectSlug}/collections/${selectedCollection.slug}`,
        });
      } catch (error: any) {
        setSaveCollectionErrors(error?.messages || []);
        console.error('Error updating collection', error);
      }
    } else {
      // Validation for create mode
      if (checkIfNameExists(collections, values.name)) {
        setSaveCollectionErrors([
          {
            type: 'name',
            error: `A collection with the name "${values.name}" already exists`,
          },
        ]);
        return;
      }

      if (checkIfSlugExists(collections, values.slug)) {
        setSaveCollectionErrors([
          {
            type: 'slug',
            error: `A collection with the id/slug "${values.slug}" already exists`,
          },
        ]);
        return;
      }

      try {
        const newCollection = await createMutateAsync({
          collection: {
            name: values.name,
            slug: values.slug,
            schemaId: values.schemaId,
            parameters: collectionParams,
          },
          projectId: selectedProject?.id,
          collectionReadme: collectionReadme,
        });

        notification.success({
          message: 'Collection Saved',
        });
        void history({
          to: `/orgs/${orgSlug}/projects/${projectSlug}/collections/${newCollection.slug}`,
        });
      } catch (error: any) {
        console.error('Error creating collection:', error);
        setSaveCollectionErrors(error?.messages || [{ type: 'api', error: error?.message || 'Unknown error' }]);
      }
    }
  };

  const onCancel = useCancelCallback(isEditMode);

  const href = useMemo(() => {
    const newSchemaUrl = `/orgs/${orgSlug}/schemas/_new`;
    return <a href={newSchemaUrl}>schema</a>;
  }, [orgSlug]);

  // Filter errors by type
  const nameErrors = saveCollectionErrors.filter((e: any) => e.type === 'name');
  const slugErrors = saveCollectionErrors.filter((e: any) => e.type === 'slug');
  const apiErrors = saveCollectionErrors.filter((e: any) => e.type === 'api');

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {apiErrors.length > 0 && (
        <Alert
          message="Error"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {apiErrors.map((error: any, idx: number) => (
                <li key={idx}>{error.error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          name: '',
          slug: '',
          schemaId: '',
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please input the collection name!' }]}
          validateStatus={nameErrors.length > 0 ? 'error' : undefined}
          help={nameErrors.length > 0 ? nameErrors[0].error : undefined}
        >
          <Input placeholder="Collection name" onChange={onNameChange} />
        </Form.Item>

        <Form.Item
          label="Slug / ID - CANNOT BE CHANGED AFTER CREATION"
          name="slug"
          rules={[
            { required: true, message: 'Please input the collection slug!' },
            {
              validator: function (_, value) {
                if (!value) return Promise.resolve();
                if (!validateSlug(value)) {
                  return Promise.reject(new Error(invalidSlugErrorMessage));
                }
                return Promise.resolve();
              },
            },
          ]}
          validateStatus={slugErrors.length > 0 ? 'error' : undefined}
          help={slugErrors.length > 0 ? slugErrors[0].error : undefined}
        >
          <Input placeholder="collection-slug" disabled={isEditMode} onChange={onSlugChange} />
        </Form.Item>

        {schemas?.length ? (
          <Form.Item label="Schema" name="schemaId" rules={[{ required: true, message: 'Please select a schema!' }]}>
            <Select placeholder="Select a schema" disabled={isEditMode} data-test-id="new-collection-schema-select">
              {schemas.map((schema: Schema) => (
                <Select.Option value={schema.id} key={schema.id} data-test-id="new-collection-schema-select-item">
                  {schema.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <div style={{ marginTop: 15 }}>
            <EmptyWarning message={<span>Please create a new {href} before finishing this collection</span>} />
          </div>
        )}

        <Form.Item
          label="Parameters"
          validateStatus={!isValidParameters && collectionParams.length > 0 ? 'error' : ''}
          help={
            !isValidParameters && collectionParams.length > 0
              ? 'Parameters must contain only lowercase letters, numbers, and underscores'
              : !collectionParams.length
                ? 'At least one parameter is required'
                : ''
          }
        >
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Type and press Enter to add parameters"
            value={collectionParams}
            onChange={onCollectionParamsChange}
            tokenSeparators={[',']}
          />
        </Form.Item>

        <div style={{ marginBottom: 55, marginTop: 25, position: 'relative' }}>
          <MarkdownEditor editorOptions={README_EDITOR_OPTIONS} onChange={onCollectionReadmeChange} value={collectionReadme} />
          {!collectionReadme && <div style={{ color: '#ff4d4f', fontSize: 14, marginTop: 4 }}>README is required</div>}
        </div>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}

export default NewCollection;
